import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import { selectFilterModal } from '../../selectors/modals.selector';
import { selectProductSections, selectProductsInputCategory, selectProductsInputSection, selectProductsTree } from '../../selectors/v2/product.selector';
import CloseIcon from '../../shared/icons/close.icon';
import TextIcon from '../../shared/icons/text.icon';
import { H2, H3 } from '../../shared/typography';
import { ThemeType } from '../../themeStyled';
import types from '../../types/actions';
import { ProductTree, SectionTree } from '../../types/product';

const Modal70Overlay = styled.div<{ open: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: flex-end;
	z-index: 1000;

	opacity: ${({ open }) => (open ? 1 : 0)};
	pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
	transition: opacity 0.3s ease;
`;

const Modal70Content = styled.div<{ open: boolean }>`
	width: 95%;
	height: 75%;
	background: white;
	border-radius: 10px 10px 0 0;
	padding: 20px;
	display: flex;
	flex-direction: column; /* dôležité */
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

	transform: ${({ open }) => (open ? 'translateY(0)' : 'translateY(100%)')};
	transition: transform 0.4s ease;

	/* zamedzí overflow celého modalu, scroll len vnútri tela */
	overflow: hidden;
`;

const CloseWrapper = styled.div`
	position: absolute;
	right: 20px;
	top: 30px;
`;

const ModalHeader = styled.div`
	height: 48px;

	display: flex;
	justify-content: center;
	align-items: stretch;
	margin: 10px;

	h2 {
		text-decoration: underline;
		text-underline-offset: 8px;
		//margin: 10px 0;
	}
`;
const ModalBody = styled.div`
	flex: 1; /* vyplní dostupný priestor medzi headerom a footerom */
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const ModalRow = styled.div`
	display: flex;
	flex: 1;
	gap: 10px;
	margin: 16px;

	flex-wrap: wrap;
	justify-content: center;
`;
const ModalFooter = styled.div`
	display: flex;
	justify-content: space-evenly;
	align-items: center;
	gap: 10px;
	padding-top: 16px;
	border-top: 1px solid #eee; /* voliteľné, vizuálne oddelenie */
`;

const FilterModal = () => {
	const modal = useSelector(selectFilterModal);
	const categories = useSelector(selectProductsTree);
	const sections = useSelector(selectProductSections);
	const dispatch = useDispatch();
	const theme = useTheme() as ThemeType;

	const SECactiveCategory = useSelector(selectProductsInputCategory);
	const SECactiveSection = useSelector(selectProductsInputSection);

	const [activeCategory, setActiveCategory] = useState<string[]>([]);
	const [activeSection, setActiveSection] = useState<string[]>([]);

	useEffect(() => {
		if (Array.isArray(SECactiveCategory)) {
			setActiveCategory(SECactiveCategory);
		}
		if (Array.isArray(SECactiveSection)) {
			setActiveSection(SECactiveSection);
		}
	}, [SECactiveCategory, SECactiveSection]);

	const [disabled, setDisabled] = useState(true);

	useEffect(() => {
		document.body.style.overflow = modal ? 'hidden' : 'auto';
		return () => {
			document.body.style.overflow = 'auto';
		};
	}, [modal]);

	useEffect(() => {
		if (activeCategory.length >= 1 || activeSection.length >= 1) {
			setDisabled(false);
		} else {
			setDisabled(true);
		}
	}, [activeCategory, activeSection, setDisabled]);

	// Klik mimo modalu → zavrieť
	const handleOverlayClick = () => {
		debugger;
		dispatch({ type: types.CLOSE_MODAL_FILTER });
	};

	const handleDelete = useCallback(() => {
		dispatch({ type: types.SET_PRODUCTS_FILTER_MODAL, payload: { category: [], section: [] } });
		dispatch({ type: types.CLOSE_MODAL_FILTER });
	}, [dispatch]);

	const handleSearch = useCallback(() => {
		dispatch({
			type: types.SET_PRODUCTS_FILTER_MODAL,
			payload: { input: '', category: activeCategory || [], section: activeSection || [] },
		});
		dispatch({ type: types.CLOSE_MODAL_FILTER });
	}, [dispatch, activeSection, activeCategory]);

	// Zastavenie bublania kliknutia vo vnútri modalu
	const handleContentClick = (e: React.MouseEvent) => {
		e.stopPropagation();
	};

	const handleCategorySelect = useCallback((category: ProductTree) => {
		setActiveSection([]);
		setActiveCategory((prev) => (prev.includes(category.code) ? prev.filter((code) => code !== category.code) : [...prev, category.code]));
	}, []);

	const handleSectionSelect = useCallback((section: SectionTree) => {
		setActiveCategory([]);
		setActiveSection((prev) => (prev.includes(section.code) ? prev.filter((code) => code !== section.code) : [...prev, section.code]));
	}, []);

	return (
		<Modal70Overlay id='FilterMenuID' onClick={handleOverlayClick} open={modal}>
			<Modal70Content onClick={handleContentClick} open={modal}>
				<ModalHeader>
					<H2>Filter</H2>
					<CloseWrapper>
						<CloseIcon onClick={() => dispatch({ type: types.CLOSE_MODAL_FILTER })} />
					</CloseWrapper>
				</ModalHeader>
				<ModalBody>
					<H3>Kategorie</H3>
					<ModalRow>
						{categories.map((category, index) => (
							<TextIcon key={index} onClick={() => handleCategorySelect(category)} selected={activeCategory.includes(category.code)} title={category.name} />
						))}
					</ModalRow>
					<H3>Vyrobky</H3>
					<ModalRow>
						{sections.map((section, index) => (
							<TextIcon key={index} onClick={() => handleSectionSelect(section)} selected={activeSection.includes(section.code)} title={section.name} />
						))}
					</ModalRow>
				</ModalBody>
				<ModalFooter>
					<TextIcon fontSize={1} hoverColor={theme.colors.erik.red} onClick={handleDelete} title={'Vymazať'} />
					<TextIcon disabled={disabled} fontSize={1} hoverColor={theme.colors.erik.green} onClick={handleSearch} title={'Vyhľadať'} />
				</ModalFooter>
			</Modal70Content>
		</Modal70Overlay>
	);
};

export default FilterModal;
